import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import { resolveDisplayLocale, SUPPORTED_LOCALES, type Locale } from '@emr/shared';

import en_access from '../locales/en/access.json';
import en_aesthetics from '../locales/en/aesthetics.json';
import en_appearance from '../locales/en/appearance.json';
import en_auditLog from '../locales/en/auditLog.json';
import en_bodyMapPicker from '../locales/en/bodyMapPicker.json';
import en_branding from '../locales/en/branding.json';
import en_calendarSync from '../locales/en/calendarSync.json';
import en_common from '../locales/en/common.json';
import en_dateFormat from '../locales/en/dateFormat.json';
import en_dermatology from '../locales/en/dermatology.json';
import en_environmentBanner from '../locales/en/environmentBanner.json';
import en_forms from '../locales/en/forms.json';
import en_gastroenterology from '../locales/en/gastroenterology.json';
import en_gatedPages from '../locales/en/gatedPages.json';
import en_help from '../locales/en/help.json';
import en_inbox from '../locales/en/inbox.json';
import en_kiosk from '../locales/en/kiosk.json';
import en_licenseScan from '../locales/en/licenseScan.json';
import en_login from '../locales/en/login.json';
import en_notFound from '../locales/en/notFound.json';
import en_orthopedics from '../locales/en/orthopedics.json';
import en_overview from '../locales/en/overview.json';
import en_patientChart from '../locales/en/patientChart.json';
import en_patientChartTabs from '../locales/en/patientChartTabs.json';
import en_patientDuplicates from '../locales/en/patientDuplicates.json';
import en_patients from '../locales/en/patients.json';
import en_pediatrics from '../locales/en/pediatrics.json';
import en_physicalTherapy from '../locales/en/physicalTherapy.json';
import en_platform from '../locales/en/platform.json';
import en_portalAccount from '../locales/en/portalAccount.json';
import en_portalAuth from '../locales/en/portalAuth.json';
import en_portalHelp from '../locales/en/portalHelp.json';
import en_portalHome from '../locales/en/portalHome.json';
import en_portalMessages from '../locales/en/portalMessages.json';
import en_portalRecords from '../locales/en/portalRecords.json';
import en_portalSchedule from '../locales/en/portalSchedule.json';
import en_portalShell from '../locales/en/portalShell.json';
import en_rcm from '../locales/en/rcm.json';
import en_schedule from '../locales/en/schedule.json';
import en_shell from '../locales/en/shell.json';
import en_signature from '../locales/en/signature.json';
import en_specialtyModules from '../locales/en/specialtyModules.json';
import es_access from '../locales/es/access.json';
import es_aesthetics from '../locales/es/aesthetics.json';
import es_appearance from '../locales/es/appearance.json';
import es_auditLog from '../locales/es/auditLog.json';
import es_bodyMapPicker from '../locales/es/bodyMapPicker.json';
import es_branding from '../locales/es/branding.json';
import es_calendarSync from '../locales/es/calendarSync.json';
import es_common from '../locales/es/common.json';
import es_dateFormat from '../locales/es/dateFormat.json';
import es_dermatology from '../locales/es/dermatology.json';
import es_environmentBanner from '../locales/es/environmentBanner.json';
import es_forms from '../locales/es/forms.json';
import es_gastroenterology from '../locales/es/gastroenterology.json';
import es_gatedPages from '../locales/es/gatedPages.json';
import es_help from '../locales/es/help.json';
import es_inbox from '../locales/es/inbox.json';
import es_kiosk from '../locales/es/kiosk.json';
import es_licenseScan from '../locales/es/licenseScan.json';
import es_login from '../locales/es/login.json';
import es_notFound from '../locales/es/notFound.json';
import es_orthopedics from '../locales/es/orthopedics.json';
import es_overview from '../locales/es/overview.json';
import es_patientChart from '../locales/es/patientChart.json';
import es_patientChartTabs from '../locales/es/patientChartTabs.json';
import es_patientDuplicates from '../locales/es/patientDuplicates.json';
import es_patients from '../locales/es/patients.json';
import es_pediatrics from '../locales/es/pediatrics.json';
import es_physicalTherapy from '../locales/es/physicalTherapy.json';
import es_platform from '../locales/es/platform.json';
import es_portalAccount from '../locales/es/portalAccount.json';
import es_portalAuth from '../locales/es/portalAuth.json';
import es_portalHelp from '../locales/es/portalHelp.json';
import es_portalHome from '../locales/es/portalHome.json';
import es_portalMessages from '../locales/es/portalMessages.json';
import es_portalRecords from '../locales/es/portalRecords.json';
import es_portalSchedule from '../locales/es/portalSchedule.json';
import es_portalShell from '../locales/es/portalShell.json';
import es_rcm from '../locales/es/rcm.json';
import es_schedule from '../locales/es/schedule.json';
import es_shell from '../locales/es/shell.json';
import es_signature from '../locales/es/signature.json';
import es_specialtyModules from '../locales/es/specialtyModules.json';

/**
 * i18next wiring — `BACKLOG.md` B7.
 *
 * Every namespace is statically imported rather than loaded per-namespace with a dynamic
 * `import()` — a deliberate simplification at this scale, worth revisiting once namespace count
 * makes it non-trivial. This list is deliberately every
 * namespace file under `web/src/locales/en/` — kept centrally, in this one file, rather than each
 * page wiring its own resources, precisely so a new namespace pair has exactly one place to
 * register.
 *
 * No `i18next-browser-languagedetector`. Deliberately: that library runs its own independent
 * detection order (querystring/cookie/localStorage/navigator), which would compete with rather
 * than compose with `@emr/shared`'s own `resolveDisplayLocale` chain (account preference, once a
 * session exists, then `navigator.languages`, then English) — one pure function drives
 * `i18next.changeLanguage()` directly instead of two fallback mechanisms layered on each other.
 *
 * Initialized synchronously at module load, imported once from `main.tsx` before the first render,
 * with only the browser-detection half of the chain available (no session exists yet at boot) —
 * `applyLocale` below is what layers the account preference on top, once one is known.
 */

void i18next.use(initReactI18next).init({
  resources: {
    en: {
      access: en_access,
      aesthetics: en_aesthetics,
      appearance: en_appearance,
      auditLog: en_auditLog,
      bodyMapPicker: en_bodyMapPicker,
      branding: en_branding,
      calendarSync: en_calendarSync,
      common: en_common,
      dateFormat: en_dateFormat,
      dermatology: en_dermatology,
      environmentBanner: en_environmentBanner,
      forms: en_forms,
      gastroenterology: en_gastroenterology,
      gatedPages: en_gatedPages,
      help: en_help,
      inbox: en_inbox,
      kiosk: en_kiosk,
      licenseScan: en_licenseScan,
      login: en_login,
      notFound: en_notFound,
      orthopedics: en_orthopedics,
      overview: en_overview,
      patientChart: en_patientChart,
      patientChartTabs: en_patientChartTabs,
      patientDuplicates: en_patientDuplicates,
      patients: en_patients,
      pediatrics: en_pediatrics,
      physicalTherapy: en_physicalTherapy,
      platform: en_platform,
      portalAccount: en_portalAccount,
      portalAuth: en_portalAuth,
      portalHelp: en_portalHelp,
      portalHome: en_portalHome,
      portalMessages: en_portalMessages,
      portalRecords: en_portalRecords,
      portalSchedule: en_portalSchedule,
      portalShell: en_portalShell,
      rcm: en_rcm,
      schedule: en_schedule,
      shell: en_shell,
      signature: en_signature,
      specialtyModules: en_specialtyModules,
    },
    es: {
      access: es_access,
      aesthetics: es_aesthetics,
      appearance: es_appearance,
      auditLog: es_auditLog,
      bodyMapPicker: es_bodyMapPicker,
      branding: es_branding,
      calendarSync: es_calendarSync,
      common: es_common,
      dateFormat: es_dateFormat,
      dermatology: es_dermatology,
      environmentBanner: es_environmentBanner,
      forms: es_forms,
      gastroenterology: es_gastroenterology,
      gatedPages: es_gatedPages,
      help: es_help,
      inbox: es_inbox,
      kiosk: es_kiosk,
      licenseScan: es_licenseScan,
      login: es_login,
      notFound: es_notFound,
      orthopedics: es_orthopedics,
      overview: es_overview,
      patientChart: es_patientChart,
      patientChartTabs: es_patientChartTabs,
      patientDuplicates: es_patientDuplicates,
      patients: es_patients,
      pediatrics: es_pediatrics,
      physicalTherapy: es_physicalTherapy,
      platform: es_platform,
      portalAccount: es_portalAccount,
      portalAuth: es_portalAuth,
      portalHelp: es_portalHelp,
      portalHome: es_portalHome,
      portalMessages: es_portalMessages,
      portalRecords: es_portalRecords,
      portalSchedule: es_portalSchedule,
      portalShell: es_portalShell,
      rcm: es_rcm,
      schedule: es_schedule,
      shell: es_shell,
      signature: es_signature,
      specialtyModules: es_specialtyModules,
    },
  },
  lng: resolveDisplayLocale(null, navigator.languages),
  fallbackLng: 'en',
  supportedLngs: [...SUPPORTED_LOCALES],
  defaultNS: 'shell',
  interpolation: { escapeValue: false }, // React already escapes.
  returnNull: false,
});

/**
 * Re-resolves the active language against an account preference (`null` once signed out, or for a
 * user who has never chosen one) plus the browser's own languages. Called from
 * `useSyncAccountLocale` whenever the account-preference query settles, and from the language
 * picker itself right after a successful write, so the UI updates immediately rather than waiting
 * for the next query refetch.
 */
export function applyLocale(accountLocale: Locale | null): void {
  const resolved = resolveDisplayLocale(accountLocale, navigator.languages);
  if (i18next.language !== resolved) {
    void i18next.changeLanguage(resolved);
  }
}

export { i18next };
